/**
 * Support ride page view-model (W1-ADMIN, D358). The Admin is the pilot's support console: an agent finds a ride by id,
 * ride code, rider/driver account id or E.164 phone, reads its timeline, fare, payment, dispatch attempts, people and
 * cases, and — only through the existing audited route — cancels it with a stable reason code. Pure and tested; the
 * screens render it and the server (`/v1/ops/ride-support/*`, `ride:view`) is the authority.
 */
import { OPS_CANCEL_NOTE_MAX_LENGTH, OPS_CANCEL_REASON_CODES, isStorableOpsCancelNote, type OperatingMarketView, type OpsCancelInput, type OpsCancelReasonCode } from '@vello/api-client';
import { marketsOfRegion } from './scope.js';
import { type OperatorScope } from './shell.js';

export type RideTone = 'neutral' | 'success' | 'warning' | 'danger';

export function rideStatusTone(status: string): RideTone {
  switch (status) {
    case 'completed':
      return 'success';
    case 'cancelled':
    case 'no_driver':
      return 'danger';
    case 'requested':
    case 'searching':
      return 'warning';
    default:
      return 'neutral';
  }
}

/** A ride can still be cancelled by an operator (the server refuses a terminal ride with 409 anyway). */
export function rideCancellable(status: string): boolean {
  return status !== 'completed' && status !== 'cancelled';
}

export type SupportQueryKind = 'empty' | 'ride_id' | 'ride_code' | 'account_id_or_phone' | 'national_phone' | 'unsupported';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const RIDE_CODE = /^R[0-9A-F]{8}$/i;

/**
 * Pre-classify what the agent typed, so the screen can ask for a market before searching (only a ride id finds its own
 * market) and explain a phone without its country calling code instead of guessing one. A UUID may be a ride id or an
 * account id — the server tries the ride first.
 */
export function classifySupportQuery(raw: string): SupportQueryKind {
  const q = raw.trim();
  if (q === '') return 'empty';
  if (UUID.test(q)) return 'ride_id';
  if (RIDE_CODE.test(q)) return 'ride_code';
  if (/^\+[\d\s().-]{7,}$/.test(q)) return 'account_id_or_phone';
  if (/^[\d\s().-]{7,}$/.test(q)) return 'national_phone';
  return 'unsupported';
}

/** Whether a search needs a market selected (every query except a ride id / UUID). */
export function supportQueryNeedsMarket(kind: SupportQueryKind): boolean {
  return kind === 'ride_code' || kind === 'account_id_or_phone';
}

/**
 * The market a support search defaults to: the scope's city, when the operator is scoped to one; for a state, its only market
 * (a state with several markets leaves the choice to the operator - never defaults to one of them at random).
 */
export function defaultSupportMarket(scope: OperatorScope, markets: readonly OperatingMarketView[] = []): string | undefined {
  if (scope.city !== undefined) return scope.city;
  if (scope.region !== undefined) {
    const inState = marketsOfRegion(scope.region, markets);
    return inState.length === 1 ? inState[0]?.id : undefined;
  }
  return undefined;
}

/**
 * Stable operator cancellation reason codes — the server's closed vocabulary (`@vello/api-client`, mirroring
 * `@vello/ride`). Labels come from the catalog (`ride.cancel.reason.<code>`); the code is what the ride keeps.
 */
export { OPS_CANCEL_REASON_CODES, OPS_CANCEL_NOTE_MAX_LENGTH, type OpsCancelReasonCode, type OpsCancelInput } from '@vello/api-client';

/**
 * Why a cancel draft cannot be sent yet: no reason picked, an internal note over the server's bound, or a note holding a
 * character the server's audit cannot keep (a control character or a broken surrogate — W5H-01).
 */
export type RideCancelDraftIssue = 'REASON_REQUIRED' | 'NOTE_TOO_LONG' | 'NOTE_INVALID';

/**
 * Validate the cancel dialog's draft (W5-HOTFIX, D382): a code from the vocabulary is mandatory; the note is optional,
 * trimmed and bounded like the server. The two travel as SEPARATE fields — the note is internal (the operator audit)
 * and never becomes part of the reason the rider, the driver or the partner desk read. Until D382 the dialog sent
 * `code: note` as one reason, which is how an agent's note reached the rider.
 */
export function validateRideCancelDraft(draft: { readonly code: string; readonly note: string }): { readonly ok: true; readonly input: OpsCancelInput } | { readonly ok: false; readonly issue: RideCancelDraftIssue } {
  const code = OPS_CANCEL_REASON_CODES.find((c) => c === draft.code);
  if (code === undefined) return { ok: false, issue: 'REASON_REQUIRED' };
  const note = draft.note.trim();
  if (note.length > OPS_CANCEL_NOTE_MAX_LENGTH) return { ok: false, issue: 'NOTE_TOO_LONG' };
  if (!isStorableOpsCancelNote(note)) return { ok: false, issue: 'NOTE_INVALID' };
  return { ok: true, input: { code, ...(note !== '' ? { note } : {}) } };
}

/** The catalog id of an operator cancel reason code's label. */
export function cancelReasonMessageId(code: OpsCancelReasonCode): string {
  return `ride.cancel.reason.${code}`;
}

/** Where a linked case opens in the console. */
export function caseRoute(type: 'safety' | 'support'): string {
  return type === 'safety' ? 'safety' : 'cases';
}

// ---- A person filter carried into the rides screen by the URL (`?person=<account id>&role=RIDER|DRIVER`) --------------------

/** Which side of a ride the filtered person was on: the rider who took it or the driver who drove it. */
export type PersonRidesRole = 'RIDER' | 'DRIVER';

/** The rides of one person, in the market of the header scope. */
export interface PersonRidesFilter {
  readonly personId: string;
  readonly role: PersonRidesRole;
}

/** The URL query that opens the rides screen already filtered to a person. */
export function personRidesFilterQuery(filter: PersonRidesFilter): Readonly<Record<string, string>> {
  return { person: filter.personId, role: filter.role };
}

/** An account id as a URL may carry it: letters, digits, `-` and `_` only, bounded (a UUID fits). Anything else is not a filter. */
const FILTER_PERSON_ID = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * Read the person filter back from a URL query. A missing, malformed or unknown value is no filter at all (the screen opens
 * unfiltered) — a link can narrow the list, never break the screen. The server still authorizes every read.
 */
export function parsePersonRidesFilter(query: Readonly<Record<string, string>>): PersonRidesFilter | undefined {
  const personId = query.person;
  const role = query.role;
  if (personId === undefined || !FILTER_PERSON_ID.test(personId)) return undefined;
  if (role !== 'RIDER' && role !== 'DRIVER') return undefined;
  return { personId, role };
}
